/**
 * LadderView — Canon · Values · the single legibility surface (iter86.21.4).
 * Renders the full ladder: Value (belief) → Standards (testable
 * expectations, with TCL chips) → Checks (enforcement signals).
 * Product-specific / game-specific are first-class labels, not warnings.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

// Layer micro-tags — a reader should know which ladder rung they're on
// without inferring it: TCL (violet) → VALUE (emerald) → STANDARD (cyan) → CHECK (zinc).
const LayerTag = ({ layer }) => ({
    tcl: <span className="text-[8px] font-extrabold uppercase tracking-wider text-violet-400 border border-violet-800 rounded px-1 py-px">TCL</span>,
    value: <span className="text-[8px] font-extrabold uppercase tracking-wider text-emerald-400 border border-emerald-800 rounded px-1 py-px">Value</span>,
    standard: <span className="text-[8px] font-extrabold uppercase tracking-wider text-cyan-400 border border-cyan-800 rounded px-1 py-px">Standard</span>,
    check: <span className="text-[8px] font-extrabold uppercase tracking-wider text-zinc-500 border border-zinc-700 rounded px-1 py-px">Checks</span>,
}[layer]);

// Upstream references, labeled as such. TCL commitments are the PARENT a
// standard expresses — a reference field, never an alias for the standard.
function UpstreamRefs({ std }) {
    if (std.tcl_classification === "product-specific") {
        return (
            <div className="text-[9px] mt-1" data-testid={`ladder-upstream-${std.standard_id}`}>
                <span className="text-zinc-500 font-bold uppercase">Expresses:</span>{" "}
                <span className="pill text-[8px] font-bold uppercase border bg-zinc-800 text-zinc-300 border-zinc-600">product-specific — no upstream parent (final)</span>
            </div>
        );
    }
    if (std.expresses_tcl.length === 0) {
        return (
            <div className="text-[9px] mt-1" data-testid={`ladder-upstream-${std.standard_id}`}>
                <span className="text-zinc-500 font-bold uppercase">Expresses:</span>{" "}
                <span className="pill text-[8px] font-bold uppercase border bg-amber-900/40 text-amber-200 border-amber-700">undispositioned</span>
            </div>
        );
    }
    return (
        <div className="text-[9px] mt-1 flex items-center gap-1.5 flex-wrap" data-testid={`ladder-upstream-${std.standard_id}`}>
            <span className="text-zinc-500 font-bold uppercase">Expresses:</span>
            {std.expresses_tcl.map((t) => (
                <span key={t} className="inline-flex items-center gap-1 bg-violet-950/40 border border-violet-900 rounded px-1 py-px">
                    <LayerTag layer="tcl" />
                    <code className="text-[9px] text-violet-300">↑ {t}</code>
                </span>
            ))}
        </div>
    );
}

export function LadderView({ data }) {
    const [mapping, setMapping] = useState(null);
    const [error, setError] = useState(null);
    useEffect(() => {
        api.tclMapping().then(setMapping)
            .catch((e) => setError(e?.response?.data?.detail || "Failed to load standards catalog"));
    }, []);
    if (error) return <div className="text-xs text-rose-300">{error}</div>;
    if (!mapping) return <div className="text-xs text-zinc-500">Loading ladder…</div>;

    const catalog = mapping.standards_catalog || [];
    if (catalog.length === 0) {
        return <div className="text-xs text-zinc-500">Standards Catalog not committed yet — approve the consolidation in Stewardship → TCL Mapping.</div>;
    }
    const groups = Object.keys(data.value_groups || {}).filter((s) => data.value_groups[s]?.statement);
    const byValue = (vg) => catalog.filter((s) => s.value_group === vg);
    const unassigned = catalog.filter((s) => !s.value_group && s.scope !== "game-specific");
    const gameSpecific = catalog.filter((s) => !s.value_group && s.scope === "game-specific");

    const StdBlock = ({ std }) => (
        <div className="border border-zinc-800 rounded p-2.5 ml-4" data-testid={`ladder-standard-${std.standard_id}`}>
            <div className="flex items-center gap-2 flex-wrap">
                <LayerTag layer="standard" />
                <code className="text-[11px] font-bold text-cyan-300">{std.standard_id}</code>
            </div>
            <div className="text-[10px] text-zinc-400 mt-0.5">{std.statement}</div>
            <UpstreamRefs std={std} />
            <div className="text-[9px] text-zinc-600 mt-1 flex items-center gap-1.5 flex-wrap">
                <LayerTag layer="check" />
                {std.signals.map((s) => <code key={s}>{s}</code>)}
            </div>
        </div>
    );

    return (
        <div className="space-y-4" data-testid="ladder-view">
            <div className="text-[10px] text-zinc-500">
                The ladder: <b className="text-zinc-300">TCL commitment</b> (why the Lab cares, optional) → <b className="text-zinc-300">Value</b> (belief the crew holds) → <b className="text-zinc-300">Standard</b> (testable expectation) → <b className="text-zinc-300">Check</b> (code that proves it every release).
            </div>
            {groups.map((vg) => {
                const stds = byValue(vg);
                return (
                    <div key={vg} data-testid={`ladder-value-${vg}`}>
                        <div className="flex items-baseline gap-2 flex-wrap">
                            <LayerTag layer="value" />
                            <code className="text-xs font-extrabold text-emerald-300">{vg}</code>
                            <span className="text-[10px] text-zinc-400 italic">“{data.value_groups[vg].statement}”</span>
                        </div>
                        <div className="space-y-1.5 mt-1.5">
                            {stds.length > 0
                                ? stds.map((s) => <StdBlock key={s.standard_id} std={s} />)
                                : <div className="text-[10px] text-zinc-600 ml-4">no standards assigned yet — approve the value cascade in Stewardship → TCL Mapping</div>}
                        </div>
                    </div>
                );
            })}
            {gameSpecific.length > 0 && (
                <div data-testid="ladder-game-specific">
                    <div className="text-xs font-extrabold text-zinc-400 uppercase">Game-specific (no value — by design)</div>
                    <div className="space-y-1.5 mt-1.5">{gameSpecific.map((s) => <StdBlock key={s.standard_id} std={s} />)}</div>
                </div>
            )}
            {unassigned.length > 0 && (
                <div data-testid="ladder-unassigned">
                    <div className="text-xs font-extrabold text-amber-300 uppercase">Awaiting value assignment</div>
                    <div className="space-y-1.5 mt-1.5">{unassigned.map((s) => <StdBlock key={s.standard_id} std={s} />)}</div>
                </div>
            )}
        </div>
    );
}
